// src/scripts/sharedHelpers/auditColours.js

/* =========================================================
   TEXT colours (existing behaviour — leave unchanged)
   ========================================================= */
export function venueAuditColour(scorePct) {
  const v = Number(scorePct) || 0;
  if (v >= 90) return "#1b5e20"; // green darken-4
  if (v >= 80) return "#ff9100"; // orange accent-3
  return "#b71c1c"; // red darken-4
}

export function adminAuditColour(scorePct) {
  const v = Number(scorePct) || 0;
  if (v === 100) return "#1b5e20"; // green darken-4
  if (v >= 95) return "#ff9100"; // orange accent-3
  return "#b71c1c"; // red darken-4
}

/* =========================================================
   BACKGROUND colours
   ========================================================= */
export const AUDIT_BG_GREEN = "#c8e6c9";
export const AUDIT_BG_ORANGE = "#ffe0b2";
export const AUDIT_BG_RED = "#ffcdd2";

export function venueAuditBackground(scorePct, hasScore) {
  if (!hasScore) return "";
  const v = Number(scorePct) || 0;
  if (v >= 90) return AUDIT_BG_GREEN;
  if (v >= 80) return AUDIT_BG_ORANGE;
  return AUDIT_BG_RED;
}

export function adminAuditBackground(scorePct, hasScore) {
  if (!hasScore) return "";
  const v = Number(scorePct) || 0;
  if (v === 100) return AUDIT_BG_GREEN;
  if (v >= 95) return AUDIT_BG_ORANGE;
  return AUDIT_BG_RED;
}

/* =========================================================
   RAG SYSTEM
   ========================================================= */
export const RAG = Object.freeze({
  GREEN: "green",
  AMBER: "amber",
  RED: "red",
  GREY: "grey",
});

export function ragLabel(rag) {
  const r = String(rag || "").toLowerCase();
  if (r === "green") return "GREEN";
  if (r === "amber" || r === "orange") return "AMBER";
  if (r === "red") return "RED";
  return "—";
}

export function ragBackground(rag) {
  const r = String(rag || "").toLowerCase();
  if (r === "green") return AUDIT_BG_GREEN;
  if (r === "amber" || r === "orange") return AUDIT_BG_ORANGE;
  if (r === "red") return AUDIT_BG_RED;
  return "";
}

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function ragLowerIsBetter(value, thresholds) {
  const n = asNumber(value);
  if (n === null) return RAG.GREY;

  const greenMax = asNumber(thresholds?.greenMax);
  const amberMax = asNumber(thresholds?.amberMax);

  if (greenMax !== null && n <= greenMax) return RAG.GREEN;
  if (amberMax !== null && n <= amberMax) return RAG.AMBER;
  return RAG.RED;
}

export function ragHigherIsBetter(value, thresholds) {
  const n = asNumber(value);
  if (n === null) return RAG.GREY;

  const greenMin = asNumber(thresholds?.greenMin);
  const amberMin = asNumber(thresholds?.amberMin);

  if (greenMin !== null && n >= greenMin) return RAG.GREEN;
  if (amberMin !== null && n >= amberMin) return RAG.AMBER;
  return RAG.RED;
}

export function ragAbsVariance(value, thresholds) {
  const n = asNumber(value);
  if (n === null) return RAG.GREY;

  const abs = Math.abs(n);
  const greenAbsMax = asNumber(thresholds?.greenAbsMax);
  const amberAbsMax = asNumber(thresholds?.amberAbsMax);

  if (greenAbsMax !== null && abs <= greenAbsMax) return RAG.GREEN;
  if (amberAbsMax !== null && abs <= amberAbsMax) return RAG.AMBER;
  return RAG.RED;
}

export function ragDirectionalVariance(value, thresholds) {
  const n = asNumber(value);
  if (n === null) return RAG.GREY;

  const badDirection = thresholds?.badDirection || "positive";
  const isBad = badDirection === "positive" ? n > 0 : n < 0;

  if (!isBad) return RAG.GREEN;
  return ragAbsVariance(n, thresholds);
}

/* =========================================================
   Compatibility colour maps
   =========================================================
   Some older screens inspect shared colour objects by several historic names.
   Export one canonical object under those aliases to remove webpack warnings.
*/
export const auditColours = Object.freeze({
  green: AUDIT_BG_GREEN,
  GREEN: AUDIT_BG_GREEN,
  approved: AUDIT_BG_GREEN,
  APPROVED: AUDIT_BG_GREEN,
  success: AUDIT_BG_GREEN,
  pass: AUDIT_BG_GREEN,
  good: AUDIT_BG_GREEN,

  amber: AUDIT_BG_ORANGE,
  AMBER: AUDIT_BG_ORANGE,
  orange: AUDIT_BG_ORANGE,
  ORANGE: AUDIT_BG_ORANGE,
  review: AUDIT_BG_ORANGE,
  REVIEW: AUDIT_BG_ORANGE,
  warning: AUDIT_BG_ORANGE,

  red: AUDIT_BG_RED,
  RED: AUDIT_BG_RED,
  rejected: AUDIT_BG_RED,
  REJECTED: AUDIT_BG_RED,
  danger: AUDIT_BG_RED,
  fail: AUDIT_BG_RED,
  bad: AUDIT_BG_RED,
});

export const auditColors = auditColours;
export const AUDIT_COLOURS = auditColours;
export const AUDIT_COLORS = auditColours;
export const RAG_COLOURS = auditColours;
export const RAG_COLORS = auditColours;

export default auditColours;
